import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Form,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  TabContent,
  TabPane,
  Alert,
} from "reactstrap";
import { Link } from "react-router-dom";

import HelpArticleLink from "../Common/HelpArticleLink";
import { canExportClinicalRecords, canManageClinicalRecords } from "../../helpers/clinicalRecordAccess";
import {
  loadEstablishmentSignature,
  saveEstablishmentSignature,
} from "../../helpers/establishmentSignaturePreference";
import { getStoredAuth } from "../../helpers/auth_storage";
import { HELP_ARTICLE_CORRECT_HEALTH_RECORD } from "../../helpers/helpCenter";
import CopyAnamnesisToServicesModal from "./CopyAnamnesisToServicesModal";
import ConsentTermPreviewModal from "./ConsentTermPreviewModal";
import ClientRecordAttachmentsPanel from "./ClientRecordAttachmentsPanel";
import EvolutionSessionsCompare from "./EvolutionSessionsCompare";
import SignaturePad from "./SignaturePad";

import {
  ANAMNESIS_TEXTAREA_CHAR_COUNT_THRESHOLD,
  ANAMNESIS_TEXTAREA_FILL_ROWS,
  ANAMNESIS_TEXTAREA_MAX_CHARS,
  ClientRecord,
  ClientRecordListParams,
  ClientRecordType,
  collectConsentText,
  createRecord,
  exportRecordPdf,
  exportRecordsBatchPdf,
  getRecordsByClient,
  isAnamnesisAnswerableFieldType,
  parseSchemaFields,
  ParsedSchemaField,
  storeConsent,
  templateRequiresConsent,
  updateRecord,
} from "../../services/clientRecordService";
import { getImageMarketingConsent } from "../../services/clientImageMarketingConsentService";
import { getSettings } from "../../services/settingsService";
import {
  ClientRecordTemplate,
  getAll as getTemplates,
} from "../../services/clientRecordTemplateService";
import { getServices, Service } from "../../services/serviceService";

const LIST_PER_PAGE = 50;
const TEMPLATE_PAGE_SIZE = 100;

type AnamnesisTab = "new" | "history";

type FormIntent = "create" | "correct" | "copy";

type PendingConfirm = "close" | "history" | "reload";

/** History filter when tenant uses per_client_service and Offcanvas is not locked to one service. */
type HistoryServiceFilter = "all" | "general" | number;

type RecordTypeFilter = "all" | ClientRecordType;

type FormKind = "anamnesis" | "evolution";

const answerComparable = (value: unknown): string => {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

const SALON_TIMEZONE_FALLBACK = "America/Sao_Paulo";

type RecordExportError = {
  data?: string;
  code?: string;
};

type AnamnesisApiError = {
  data?: string;
  code?: string;
  details?: Record<string, string[]>;
};

const extractFirstValidationMessage = (error: AnamnesisApiError): string | null => {
  const details = error.details;

  if (!details) {
    return null;
  }

  for (const messages of Object.values(details)) {
    if (Array.isArray(messages) && messages.length > 0 && messages[0]) {
      return messages[0];
    }
  }

  return null;
};

const resolveAnamnesisSaveErrorMessage = (
  error: AnamnesisApiError,
  fallback: string,
  t: (key: string) => string
): string => {
  if (error.code === "CLINICAL_RECORD_STALE") {
    return t("clients.anamnesis.banner.stale");
  }

  if (error.code === "NOT_FOUND") {
    return t("clients.anamnesis.toast.recordMissing");
  }

  if (error.code === "FORBIDDEN") {
    return t("clients.anamnesis.toast.forbidden");
  }

  if (error.code === "VALIDATION_ERROR") {
    const firstDetail = extractFirstValidationMessage(error);

    if (firstDetail) {
      return firstDetail;
    }
  }

  return error.data || fallback;
};

const resolveRecordExportErrorMessage = (
  error: RecordExportError,
  t: (key: string) => string
): string => {
  if (error.code === "FORBIDDEN") {
    return t("clients.records.toast.exportForbidden");
  }

  return error.data || t("clients.records.toast.exportError");
};

type ClientAnamnesisOffcanvasProps = {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  clientName: string;
  initialTemplateId?: number | null;
  /** When set (per_client_service), new fichas are linked to this service. */
  serviceId?: number | null;
  /** Called after a new/copied record is saved (not corrections). */
  onRecordSaved?: (clientId: number) => void;
  /** Pre-fill evolution form for this appointment (agenda CTA). */
  evolutionAppointmentId?: number | null;
  /** Open history with this record expanded (view evolution). */
  initialRecordId?: number | null;
  initialRecordTypeFilter?: RecordTypeFilter;
};

const formatRecordDate = (value: string, locale: string, timeZone: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: SALON_TIMEZONE_FALLBACK,
    }).format(date);
  }
};

const wasRecordCorrected = (record: ClientRecord): boolean => {
  const created = new Date(record.created_at).getTime();
  const updated = new Date(record.updated_at).getTime();

  if (Number.isNaN(created) || Number.isNaN(updated)) {
    return false;
  }

  return updated > created;
};

const coerceBooleanAnswer = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

const emptyAnswersForFields = (
  fields: ParsedSchemaField[]
): Record<string, unknown> => {
  const answers: Record<string, unknown> = {};

  fields.forEach((field) => {
    if (!isAnamnesisAnswerableFieldType(field.type)) {
      return;
    }

    if (field.type === "boolean" || field.type === "consent_checkbox") {
      answers[field.name] = false;
      return;
    }

    answers[field.name] = "";
  });

  return answers;
};

const highlightQuerySegments = (
  text: string,
  query: string
): Array<{ text: string; match: boolean }> => {
  const trimmed = query.trim();

  if (!trimmed || !text) {
    return [{ text, match: false }];
  }

  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);

  return parts
    .filter((part) => part.length > 0)
    .map((part) => ({
      text: part,
      match: part.toLowerCase() === trimmed.toLowerCase(),
    }));
};

const HighlightedTemplateTitle = ({
  title,
  query,
}: {
  title: string;
  query: string;
}) => (
  <>
    {highlightQuerySegments(title, query).map((segment, index) =>
      segment.match ? (
        <mark key={`${index}-${segment.text}`} className="px-0 bg-warning-subtle">
          {segment.text}
        </mark>
      ) : (
        <span key={`${index}-${segment.text}`}>{segment.text}</span>
      )
    )}
  </>
);

const ClientAnamnesisOffcanvas = ({
  isOpen,
  onClose,
  clientId,
  clientName,
  initialTemplateId = null,
  serviceId = null,
  onRecordSaved,
  evolutionAppointmentId = null,
  initialRecordId = null,
  initialRecordTypeFilter = "all",
}: ClientAnamnesisOffcanvasProps) => {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState<AnamnesisTab>("new");
  const [templates, setTemplates] = useState<ClientRecordTemplate[]>([]);
  const [records, setRecords] = useState<ClientRecord[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exportingRecordId, setExportingRecordId] = useState<number | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [formIntent, setFormIntent] = useState<FormIntent>("create");
  const [editingRecordId, setEditingRecordId] = useState<number | null>(null);
  const [orphanAnswers, setOrphanAnswers] = useState<Record<string, unknown>>({});
  const [showRemovedFieldsNotice, setShowRemovedFieldsNotice] = useState(false);
  const [salonTimezone, setSalonTimezone] = useState(SALON_TIMEZONE_FALLBACK);
  const [baselineUpdatedAt, setBaselineUpdatedAt] = useState<string | null>(null);
  const [staleNotice, setStaleNotice] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsLastPage, setRecordsLastPage] = useState(1);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [openedAnswers, setOpenedAnswers] = useState<Record<string, unknown>>({});
  const [confirmAction, setConfirmAction] = useState<PendingConfirm | null>(null);
  const [anamnesisScope, setAnamnesisScope] = useState<"per_client" | "per_client_service">(
    "per_client"
  );
  const [canUseInMarketing, setCanUseInMarketing] = useState(false);
  const [historyServiceFilter, setHistoryServiceFilter] = useState<HistoryServiceFilter>("all");
  const [catalogServices, setCatalogServices] = useState<Service[]>([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copySourceRecord, setCopySourceRecord] = useState<ClientRecord | null>(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [establishmentSignatureDataUrl, setEstablishmentSignatureDataUrl] = useState<string | null>(null);
  const [consentPreview, setConsentPreview] = useState<{ title: string; body: string } | null>(null);
  const [selectedExportIds, setSelectedExportIds] = useState<number[]>([]);
  const [exportingBatch, setExportingBatch] = useState(false);
  const [resignTarget, setResignTarget] = useState<ClientRecord | null>(null);
  const [resignReason, setResignReason] = useState("");
  const [resignSignatureDataUrl, setResignSignatureDataUrl] = useState<string | null>(null);
  const [resignEstablishmentDataUrl, setResignEstablishmentDataUrl] = useState<string | null>(null);
  const [resigning, setResigning] = useState(false);
  const [consentReadConfirmed, setConsentReadConfirmed] = useState(false);
  const preserveAnswersRef = useRef(false);
  const [recordTypeFilter, setRecordTypeFilter] = useState<RecordTypeFilter>("all");
  const [formKind, setFormKind] = useState<FormKind>("anamnesis");
  const [evolutionNotes, setEvolutionNotes] = useState("");
  const [evolutionTemplateId, setEvolutionTemplateId] = useState("");
  const [expandedRecordId, setExpandedRecordId] = useState<number | null>(null);
  const [linkedEvolutionAppointmentId, setLinkedEvolutionAppointmentId] = useState<
    number | null
  >(null);

  const authIdentity = useMemo(() => {
    const stored = getStoredAuth();
    const user = stored?.user ?? null;
    const tenantId =
      (typeof user?.tenant_id === "number" ? user.tenant_id : null) ??
      (typeof user?.current_tenant_id === "number" ? user.current_tenant_id : null);
    const userId = typeof user?.id === "number" ? user.id : null;
    return { tenantId, userId };
  }, []);

  const canExportRecords = canExportClinicalRecords();
  const canManageRecords = canManageClinicalRecords();
  const showServiceHistoryTools =
    anamnesisScope === "per_client_service" && (serviceId === null || serviceId === undefined);

  const activeTemplates = useMemo(
    () => templates.filter((template) => template.is_active),
    [templates]
  );

  const anamnesisTemplates = useMemo(
    () =>
      activeTemplates.filter(
        (template) => (template.record_type ?? "anamnesis") !== "evolution"
      ),
    [activeTemplates]
  );

  const evolutionTemplates = useMemo(
    () =>
      activeTemplates.filter(
        (template) => template.record_type === "evolution"
      ),
    [activeTemplates]
  );

  const historyEvolutions = useMemo(
    () => records.filter((record) => record.record_type === "evolution"),
    [records]
  );

  const selectedTemplate = useMemo(() => {
    const fromActive = activeTemplates.find(
      (template) => template.id === Number(selectedTemplateId)
    );

    if (fromActive) {
      return fromActive;
    }

    const fromHistory = records.find(
      (record) => record.template?.id === Number(selectedTemplateId)
    )?.template;

    return fromHistory ?? null;
  }, [activeTemplates, records, selectedTemplateId]);

  const schemaFields = useMemo(
    () => parseSchemaFields(selectedTemplate?.schema_json),
    [selectedTemplate]
  );

  const canCorrectRecord = (record: ClientRecord): boolean => {
    if (record.record_type === "evolution") {
      return false;
    }

    if (record.has_signed_consent) {
      return false;
    }

    if (typeof record.can_correct === "boolean") {
      return record.can_correct;
    }

    return canManageRecords;
  };

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);

    try {
      const response = await getTemplates({
        page: 1,
        per_page: TEMPLATE_PAGE_SIZE,
        is_active: true,
      });

      setTemplates(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("clients.anamnesis.toast.loadTemplatesError"));
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [t]);

  const fetchRecords = useCallback(async (page: number, query: string, append: boolean) => {
    setLoadingRecords(true);

    try {
      const params: ClientRecordListParams = {
        page,
        per_page: LIST_PER_PAGE,
        ...(query !== "" ? { q: query } : {}),
      };

      if (serviceId) {
        params.service_id = serviceId;
      } else if (anamnesisScope === "per_client_service") {
        if (historyServiceFilter === "general") {
          params.general_only = true;
        } else if (typeof historyServiceFilter === "number") {
          params.service_id = historyServiceFilter;
        }
      }

      if (recordTypeFilter !== "all") {
        params.record_type = recordTypeFilter;
      }

      const response = await getRecordsByClient(clientId, params);

      setRecords((current) => (append ? [...current, ...response.data] : response.data));
      setRecordsPage(response.meta?.current_page ?? page);
      setRecordsLastPage(response.meta?.last_page ?? 1);
      setRecordsTotal(response.meta?.total ?? response.data.length);
    } catch (error: any) {
      toast.error(error?.data || t("clients.anamnesis.toast.loadRecordsError"));

      if (!append) {
        setRecords([]);
        setRecordsPage(1);
        setRecordsLastPage(1);
        setRecordsTotal(0);
      }
    } finally {
      setLoadingRecords(false);
    }
  }, [anamnesisScope, clientId, historyServiceFilter, recordTypeFilter, serviceId, t]);

  const findRecordInHistory = useCallback(async (recordId: number): Promise<ClientRecord | null> => {
    let page = 1;
    let lastPage = 1;

    do {
      const response = await getRecordsByClient(clientId, {
        page,
        per_page: 100,
      });
      const found = response.data.find((record) => record.id === recordId);

      if (found) {
        return found;
      }

      lastPage = response.meta?.last_page ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 20);

    return null;
  }, [clientId]);

  const resetDraft = useCallback(() => {
    preserveAnswersRef.current = false;
    setFormIntent("create");
    setEditingRecordId(null);
    setOrphanAnswers({});
    setShowRemovedFieldsNotice(false);
    setBaselineUpdatedAt(null);
    setStaleNotice(false);
    setHistoryQuery("");
    setAppliedQuery("");
    setOpenedAnswers({});
    setSelectedTemplateId("");
    setAnswers({});
    setSignatureDataUrl(null);
    setEstablishmentSignatureDataUrl(null);
    setConsentReadConfirmed(false);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAppliedQuery(historyQuery.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [historyQuery]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    void fetchRecords(1, appliedQuery, false);
  }, [isOpen, appliedQuery, fetchRecords]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    loadTemplates();
    setActiveTab("new");
    resetDraft();
    setHistoryServiceFilter("all");
    setCopyModalOpen(false);
    setCopySourceRecord(null);
    setRecordTypeFilter(initialRecordTypeFilter);
    setEvolutionNotes("");
    setEvolutionTemplateId("");
    setExpandedRecordId(initialRecordId);
    setLinkedEvolutionAppointmentId(evolutionAppointmentId ?? null);

    if (evolutionAppointmentId) {
      setFormKind("evolution");
      setActiveTab("new");
    } else {
      setFormKind("anamnesis");
    }

    if (initialRecordId) {
      setActiveTab("history");
    }

    getSettings()
      .then((response) => {
        if (response.data?.timezone) {
          setSalonTimezone(response.data.timezone);
        }

        if (response.data?.anamnesis_scope === "per_client_service") {
          setAnamnesisScope("per_client_service");
        } else {
          setAnamnesisScope("per_client");
        }
      })
      .catch(() => {
        setSalonTimezone(SALON_TIMEZONE_FALLBACK);
        setAnamnesisScope("per_client");
      });

    getServices({ per_page: 100 })
      .then((response) => {
        setCatalogServices(response.data ?? []);
      })
      .catch(() => {
        setCatalogServices([]);
      });
  }, [
    evolutionAppointmentId,
    initialRecordId,
    initialRecordTypeFilter,
    isOpen,
    loadTemplates,
    resetDraft,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const saved = loadEstablishmentSignature(authIdentity.tenantId, authIdentity.userId);
    if (saved) {
      setEstablishmentSignatureDataUrl(saved);
    }
  }, [isOpen, authIdentity.tenantId, authIdentity.userId]);

  useEffect(() => {
    if (!isOpen || !clientId) {
      setCanUseInMarketing(false);
      return;
    }

    let cancelled = false;
    getImageMarketingConsent(clientId)
      .then((response) => {
        if (!cancelled) {
          setCanUseInMarketing(Boolean(response.data?.can_use_in_marketing));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanUseInMarketing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, clientId]);

  useEffect(() => {
    if (!isOpen || !initialTemplateId || activeTemplates.length === 0) {
      return;
    }

    const templateExists = activeTemplates.some(
      (template) => template.id === initialTemplateId
    );

    if (templateExists) {
      setSelectedTemplateId(String(initialTemplateId));
    }
  }, [isOpen, initialTemplateId, activeTemplates]);

  useEffect(() => {
    if (preserveAnswersRef.current) {
      preserveAnswersRef.current = false;
      return;
    }

    if (!selectedTemplate || formIntent !== "create") {
      if (!selectedTemplate) {
        setAnswers({});
      }

      return;
    }

    setAnswers(emptyAnswersForFields(schemaFields));
  }, [selectedTemplate, schemaFields, formIntent]);

  const handleTemplateChange = (value: string) => {
    preserveAnswersRef.current = false;
    setFormIntent("create");
    setEditingRecordId(null);
    setOrphanAnswers({});
    setShowRemovedFieldsNotice(false);
    setSelectedTemplateId(value);
  };

  const openRecordDraft = (record: ClientRecord, intent: Exclude<FormIntent, "create">) => {
    const template = record.template;

    if (!template) {
      toast.error(t("clients.anamnesis.toast.templateUnavailable"));
      return;
    }

    const fields = parseSchemaFields(template.schema_json);
    const fieldNames = new Set(fields.map((field) => field.name));
    const nextAnswers = emptyAnswersForFields(fields);
    const orphans: Record<string, unknown> = {};
    let staleOption = false;

    Object.entries(record.answers_json ?? {}).forEach(([key, value]) => {
      if (!fieldNames.has(key)) {
        orphans[key] = value;
        return;
      }

      const field = fields.find((item) => item.name === key);

      if (field?.type === "boolean") {
        nextAnswers[key] = coerceBooleanAnswer(value);
        return;
      }

      if (field?.type === "select") {
        const current = value === null || value === undefined ? "" : String(value);

        if (current !== "" && !field.options.includes(current)) {
          staleOption = true;
        }

        nextAnswers[key] = current;
        return;
      }

      nextAnswers[key] = value === null || value === undefined ? "" : value;
    });

    preserveAnswersRef.current = true;
    setFormIntent(intent);
    setEditingRecordId(intent === "correct" ? record.id : null);
    setOrphanAnswers(orphans);
    setShowRemovedFieldsNotice(Object.keys(orphans).length > 0 || staleOption);
    setBaselineUpdatedAt(intent === "correct" ? record.updated_at : null);
    setStaleNotice(false);
    setOpenedAnswers(nextAnswers);
    setSelectedTemplateId(String(template.id));
    setAnswers(nextAnswers);
    setActiveTab("new");
  };

  const loadLatestCorrection = async () => {
    if (!editingRecordId) {
      return;
    }

    try {
      const latest = await findRecordInHistory(editingRecordId);
      await fetchRecords(1, appliedQuery, false);

      if (!latest) {
        toast.error(t("clients.anamnesis.toast.recordMissing"));
        resetDraft();
        setActiveTab("history");
        return;
      }

      openRecordDraft(latest, "correct");
    } catch (error: any) {
      toast.error(error?.data || t("clients.anamnesis.toast.loadRecordsError"));
    }
  };

  const handleAnswerChange = (fieldName: string, value: unknown) => {
    setAnswers((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const fieldChanged = (fieldName: string): boolean => {
    if (formIntent === "create") {
      return false;
    }

    return answerComparable(answers[fieldName]) !== answerComparable(openedAnswers[fieldName]);
  };

  const changedFieldCount = schemaFields.filter(
    (field) => isAnamnesisAnswerableFieldType(field.type) && fieldChanged(field.name)
  ).length;

  const isDraftDirty = (): boolean => {
    if (formIntent === "create") {
      return schemaFields.some((field) => {
        if (!isAnamnesisAnswerableFieldType(field.type)) {
          return false;
        }

        const value = answers[field.name];

        if (field.type === "boolean") {
          return value === true;
        }

        return String(value ?? "").trim() !== "";
      });
    }

    return changedFieldCount > 0;
  };

  const runConfirmAction = (action: PendingConfirm) => {
    if (action === "close") {
      onClose();
      return;
    }

    if (action === "history") {
      resetDraft();
      setActiveTab("history");
      return;
    }

    void loadLatestCorrection();
  };

  const requestConfirm = (action: PendingConfirm) => {
    if (!isDraftDirty()) {
      runConfirmAction(action);
      return;
    }

    setConfirmAction(action);
  };

  const resolveFieldLabel = (
    fieldName: string,
    template?: ClientRecordTemplate | null
  ): string => {
    if (!template) {
      return fieldName;
    }

    const field = parseSchemaFields(template.schema_json).find(
      (item) => item.name === fieldName
    );

    return field?.label ?? fieldName;
  };

  const formatAnswerValue = (value: unknown, fieldName: string, template?: ClientRecordTemplate | null): string => {
    const field = template
      ? parseSchemaFields(template.schema_json).find((item) => item.name === fieldName)
      : undefined;

    if (field?.type === "boolean" || typeof value === "boolean") {
      return value ? t("clients.anamnesis.fields.booleanYes") : t("clients.anamnesis.fields.booleanNo");
    }

    if (value === null || value === undefined || value === "") {
      return t("clients.anamnesis.common.notAvailable");
    }

    return String(value);
  };

  const handleDownloadPdf = async (record: ClientRecord) => {
    setExportingRecordId(record.id);

    try {
      const blob = await exportRecordPdf(clientId, record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ficha.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(resolveRecordExportErrorMessage(error as RecordExportError, t));
    } finally {
      setExportingRecordId(null);
    }
  };

  const handleBatchExport = async () => {
    if (selectedExportIds.length === 0) {
      toast.error(t("clients.records.toast.batchEmpty"));
      return;
    }

    setExportingBatch(true);

    try {
      const blob = await exportRecordsBatchPdf(clientId, selectedExportIds);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "fichas-assinadas.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("clients.records.toast.batchSuccess"));
      setSelectedExportIds([]);
    } catch (error: any) {
      toast.error(resolveRecordExportErrorMessage(error as RecordExportError, t));
    } finally {
      setExportingBatch(false);
    }
  };

  const toggleExportSelection = (recordId: number) => {
    setSelectedExportIds((current) =>
      current.includes(recordId)
        ? current.filter((id) => id !== recordId)
        : [...current, recordId]
    );
  };

  const handleResignConsent = async () => {
    if (!resignTarget) {
      return;
    }

    const reason = resignReason.trim();
    if (reason.length < 3) {
      toast.error(t("clients.anamnesis.resign.reasonRequired"));
      return;
    }

    if (!resignSignatureDataUrl) {
      toast.error(t("clients.anamnesis.validation.signatureRequired"));
      return;
    }

    setResigning(true);

    try {
      await storeConsent(resignTarget.id, {
        consent_text_snapshot: collectConsentText(
          parseSchemaFields(resignTarget.template?.schema_json)
        ),
        signature_image_base64: resignSignatureDataUrl,
        establishment_signature_image_base64: resignEstablishmentDataUrl,
        resign: true,
        resign_reason: reason,
      });
      toast.success(t("clients.anamnesis.toast.resignSuccess"));
      setResignTarget(null);
      setResignReason("");
      setResignSignatureDataUrl(null);
      setResignEstablishmentDataUrl(null);
      await fetchRecords(1, appliedQuery, false);
    } catch (error: any) {
      toast.error(
        resolveAnamnesisSaveErrorMessage(
          error as AnamnesisApiError,
          t("clients.anamnesis.toast.resignError"),
          t
        )
      );
    } finally {
      setResigning(false);
    }
  };

  const handlePrintPdf = async (record: ClientRecord) => {
    setExportingRecordId(record.id);

    try {
      const blob = await exportRecordPdf(clientId, record.id);
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      if (!printWindow) {
        window.URL.revokeObjectURL(url);
        toast.error(t("clients.records.toast.exportError"));
        return;
      }

      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      toast.error(resolveRecordExportErrorMessage(error as RecordExportError, t));
    } finally {
      setExportingRecordId(null);
    }
  };

  const persistEvolution = async (): Promise<boolean> => {
    const appointmentId = linkedEvolutionAppointmentId;

    if (!appointmentId) {
      toast.error(t("clients.evolution.validation.appointmentRequired"));
      return false;
    }

    const trimmedNotes = evolutionNotes.trim();
    const templateNumericId =
      evolutionTemplateId !== "" ? Number(evolutionTemplateId) : null;

    if (!trimmedNotes && !templateNumericId) {
      toast.error(t("clients.evolution.validation.notesOrTemplate"));
      return false;
    }

    const payloadAnswers =
      templateNumericId && selectedTemplate
        ? { ...orphanAnswers, ...answers }
        : {};

    setSubmitting(true);

    try {
      const response = await createRecord(clientId, {
        record_type: "evolution",
        appointment_id: appointmentId,
        notes: trimmedNotes || null,
        template_id: templateNumericId,
        answers_json: payloadAnswers,
      });

      toast.success(response.message || t("clients.evolution.toast.saveSuccess"));
      onRecordSaved?.(clientId);

      const savedId = response.data.id;
      setFormKind("anamnesis");
      setEvolutionNotes("");
      setEvolutionTemplateId("");
      setLinkedEvolutionAppointmentId(null);
      setActiveTab("history");
      setExpandedRecordId(savedId);

      if (appliedQuery === "" && historyQuery.trim() === "") {
        await fetchRecords(1, "", false);
      }

      return true;
    } catch (error: unknown) {
      const apiError = error as AnamnesisApiError;
      toast.error(
        resolveAnamnesisSaveErrorMessage(
          apiError,
          t("clients.evolution.toast.saveError"),
          t
        )
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const persistDraft = async (): Promise<boolean> => {
    if (formKind === "evolution") {
      return persistEvolution();
    }

    if (!selectedTemplate) {
      toast.error(t("clients.anamnesis.validation.templateRequired"));
      return false;
    }

    const answerableFields = schemaFields.filter((field) =>
      isAnamnesisAnswerableFieldType(field.type)
    );
    if (answerableFields.length === 0 && Object.keys(orphanAnswers).length === 0) {
      toast.error(t("clients.anamnesis.validation.schemaEmpty"));
      return false;
    }

    const needsConsent = templateRequiresConsent(schemaFields);
    if (needsConsent) {
      const hasConsentText = schemaFields.some((field) => field.type === "consent_text");
      if (hasConsentText && !consentReadConfirmed) {
        toast.error(t("clients.anamnesis.validation.consentPreviewRequired"));
        return false;
      }

      const checkboxFields = schemaFields.filter(
        (field) => field.type === "consent_checkbox"
      );
      const missingCheckbox = checkboxFields.some(
        (field) => field.required && !coerceBooleanAnswer(answers[field.name])
      );
      if (missingCheckbox || (checkboxFields.length > 0 && checkboxFields.every(
        (field) => !coerceBooleanAnswer(answers[field.name])
      ))) {
        toast.error(t("clients.anamnesis.validation.consentRequired"));
        return false;
      }

      const signatureRequired = schemaFields.some(
        (field) => field.type === "signature"
      );
      if (signatureRequired && !signatureDataUrl) {
        toast.error(t("clients.anamnesis.validation.signatureRequired"));
        return false;
      }
    }

    const payloadAnswers = {
      ...orphanAnswers,
      ...answers,
    };

    setSubmitting(true);

    try {
      let savedRecordId: number | null = editingRecordId;

      if (formIntent === "correct" && editingRecordId) {
        await updateRecord(editingRecordId, {
          answers_json: payloadAnswers,
          expected_updated_at: baselineUpdatedAt ?? undefined,
        });

        toast.success(t("clients.anamnesis.toast.correctSuccess"));
      } else {
        const response = await createRecord(clientId, {
          template_id: selectedTemplate.id,
          answers_json: payloadAnswers,
          ...(serviceId ? { service_id: serviceId } : {}),
        });

        savedRecordId = response.data.id;

        toast.success(
          formIntent === "copy"
            ? t("clients.anamnesis.toast.copySuccess")
            : (response.message || t("clients.anamnesis.toast.saveSuccess"))
        );
        onRecordSaved?.(clientId);
      }

      if (needsConsent && savedRecordId) {
        await storeConsent(savedRecordId, {
          consent_text_snapshot: collectConsentText(schemaFields),
          signature_image_base64: signatureDataUrl,
          establishment_signature_image_base64: establishmentSignatureDataUrl,
        });
        saveEstablishmentSignature(
          authIdentity.tenantId,
          authIdentity.userId,
          establishmentSignatureDataUrl
        );
        toast.success(t("clients.anamnesis.toast.consentSuccess"));
      }

      resetDraft();
      setActiveTab("history");

      if (appliedQuery === "" && historyQuery.trim() === "") {
        await fetchRecords(1, "", false);
      }

      return true;
    } catch (error: any) {
      const apiError = error as AnamnesisApiError;

      if (apiError.code === "CLINICAL_RECORD_STALE") {
        setStaleNotice(true);
        return false;
      }

      toast.error(
        resolveAnamnesisSaveErrorMessage(
          apiError,
          t("clients.anamnesis.toast.saveError"),
          t
        )
      );
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await persistDraft();
  };

  const selectOptionsFor = (field: ParsedSchemaField): string[] => {
    const current = answers[field.name];
    const currentText = current === null || current === undefined ? "" : String(current);

    if (currentText === "" || field.options.includes(currentText)) {
      return field.options;
    }

    return [currentText, ...field.options];
  };

  const renderDynamicField = (field: ParsedSchemaField) => {
    const value = answers[field.name];
    const changed = fieldChanged(field.name);
    const previousAnswer = formatAnswerValue(
      openedAnswers[field.name],
      field.name,
      selectedTemplate
    );
    const changedBadge = changed ? (
      <>
        <Badge color="warning" className="ms-2 badge-soft-warning">
          {t("clients.anamnesis.fields.changed")}
        </Badge>
        <span className="d-block text-muted small mt-1">
          {t("clients.anamnesis.fields.previousAnswer", { value: previousAnswer })}
        </span>
      </>
    ) : null;
    const changedClass = changed ? "border-start border-warning border-3 ps-2" : "";

    if (field.type === "section") {
      return (
        <div className="mb-3 mt-2" key={field.name}>
          <h6 className="fw-semibold mb-1 text-body">{field.label}</h6>
          <p className="text-muted small mb-0">
            {t("clients.anamnesis.fields.sectionHint")}
          </p>
          <hr className="mt-2 mb-0" />
        </div>
      );
    }

    if (field.type === "boolean") {
      return (
        <div className={`form-check form-switch mb-3 ${changedClass}`} key={field.name}>
          <Input
            type="checkbox"
            role="switch"
            className="form-check-input"
            data-clarity-mask="true"
            id={`anamnesis-field-${field.name}`}
            checked={Boolean(value)}
            onChange={(event) =>
              handleAnswerChange(field.name, event.target.checked)
            }
          />
          <Label className="form-check-label" htmlFor={`anamnesis-field-${field.name}`}>
            {field.label}
            {changedBadge}
          </Label>
        </div>
      );
    }

    if (field.type === "consent_text") {
      return (
        <div className="mb-3" key={field.name}>
          <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
            <Label className="fw-medium mb-0">{field.label}</Label>
            <Button
              color="link"
              size="sm"
              type="button"
              className="p-0"
              onClick={() =>
                setConsentPreview({
                  title: field.label,
                  body: field.body,
                })
              }
            >
              <i className="ri-fullscreen-line me-1" />
              {t("clients.anamnesis.preview.open")}
            </Button>
          </div>
          <div
            className="border rounded p-3 bg-light small"
            style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflowY: "auto" }}
          >
            {field.body || t("clients.anamnesis.fields.consentTextEmpty")}
          </div>
        </div>
      );
    }

    if (field.type === "consent_checkbox") {
      return (
        <div className={`form-check mb-3 ${changedClass}`} key={field.name}>
          <Input
            type="checkbox"
            className="form-check-input"
            id={`anamnesis-field-${field.name}`}
            checked={Boolean(value)}
            onChange={(event) =>
              handleAnswerChange(field.name, event.target.checked)
            }
          />
          <Label className="form-check-label" htmlFor={`anamnesis-field-${field.name}`}>
            {field.label || t("clients.anamnesis.fields.consentCheckboxDefault")}
            {field.required ? " *" : ""}
          </Label>
        </div>
      );
    }

    if (field.type === "signature") {
      return (
        <div className="mb-3" key={field.name}>
          <Label className="fw-medium">
            {field.label}
            {field.required ? " *" : ""}
          </Label>
          <SignaturePad
            value={signatureDataUrl}
            onChange={setSignatureDataUrl}
            disabled={submitting}
          />
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <div className={`mb-3 ${changedClass}`} key={field.name}>
          <Label htmlFor={`anamnesis-field-${field.name}`}>
            {field.label}
            {changedBadge}
          </Label>
          <Input
            id={`anamnesis-field-${field.name}`}
            type="select"
            data-clarity-mask="true"
            value={String(value ?? "")}
            onChange={(event) =>
              handleAnswerChange(field.name, event.target.value)
            }
          >
            <option value="">{t("clients.anamnesis.fields.selectPlaceholder")}</option>
            {selectOptionsFor(field).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Input>
        </div>
      );
    }

    if (field.type === "textarea") {
      const textValue = String(value ?? "");
      const charCount = textValue.length;
      const showCharCount = charCount >= ANAMNESIS_TEXTAREA_CHAR_COUNT_THRESHOLD;
      const nearLimit = charCount >= ANAMNESIS_TEXTAREA_MAX_CHARS;

      return (
        <div className={`mb-3 ${changedClass}`} key={field.name}>
          <Label htmlFor={`anamnesis-field-${field.name}`}>
            {field.label}
            {changedBadge}
          </Label>
          <Input
            id={`anamnesis-field-${field.name}`}
            type="textarea"
            rows={ANAMNESIS_TEXTAREA_FILL_ROWS}
            maxLength={ANAMNESIS_TEXTAREA_MAX_CHARS}
            data-clarity-mask="true"
            invalid={nearLimit}
            value={textValue}
            onChange={(event) =>
              handleAnswerChange(
                field.name,
                event.target.value.slice(0, ANAMNESIS_TEXTAREA_MAX_CHARS)
              )
            }
          />
          <div className="form-text">
            {t("clients.anamnesis.fields.textareaProtocolHint")}
          </div>
          {showCharCount ? (
            <div className={`form-text ${nearLimit ? "text-danger" : "text-warning"}`}>
              {t("clients.anamnesis.fields.textareaCharCount", {
                remaining: Math.max(
                  0,
                  ANAMNESIS_TEXTAREA_MAX_CHARS - charCount
                ),
                count: charCount,
                max: ANAMNESIS_TEXTAREA_MAX_CHARS,
              })}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div className={`mb-3 ${changedClass}`} key={field.name}>
        <Label htmlFor={`anamnesis-field-${field.name}`}>
          {field.label}
          {changedBadge}
        </Label>
        <Input
          id={`anamnesis-field-${field.name}`}
          type="text"
          data-clarity-mask="true"
          value={String(value ?? "")}
          onChange={(event) =>
            handleAnswerChange(field.name, event.target.value)
          }
        />
      </div>
    );
  };

  return (
    <>
    <Offcanvas isOpen={isOpen} toggle={() => requestConfirm("close")} direction="end" className="border-0">
      <OffcanvasHeader toggle={() => requestConfirm("close")} className="bg-light">
        {t("clients.anamnesis.title", { name: clientName })}
      </OffcanvasHeader>
      <OffcanvasBody>
        <Nav tabs className="nav-tabs-custom nav-success mb-3">
          <NavItem>
            <NavLink
              href="#"
              className={activeTab === "new" ? "active" : ""}
              onClick={(event) => {
                event.preventDefault();
                setActiveTab("new");
              }}
            >
              {formKind === "evolution"
                ? t("clients.evolution.tabs.new")
                : formIntent === "correct"
                  ? t("clients.anamnesis.tabs.correct")
                  : t("clients.anamnesis.tabs.new")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              className={activeTab === "history" ? "active" : ""}
              onClick={(event) => {
                event.preventDefault();
                requestConfirm("history");
              }}
            >
              {t("clients.anamnesis.tabs.history")}
              {recordsTotal > 0 ? (
                <Badge color="success" className="ms-1">
                  {recordsTotal}
                </Badge>
              ) : null}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="new">
            {formKind === "evolution" ? (
              <Form onSubmit={handleSubmit}>
                <Alert color="info" className="py-2">
                  <p className="mb-0 small">{t("clients.evolution.form.hint")}</p>
                </Alert>
                <div className="mb-3">
                  <Label htmlFor="evolution-notes">{t("clients.evolution.form.notesLabel")}</Label>
                  <Input
                    id="evolution-notes"
                    type="textarea"
                    rows={5}
                    data-clarity-mask="true"
                    value={evolutionNotes}
                    placeholder={t("clients.evolution.form.notesPlaceholder")}
                    onChange={(event) => setEvolutionNotes(event.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div className="mb-3">
                  <Label htmlFor="evolution-template">{t("clients.evolution.form.templateLabel")}</Label>
                  <Input
                    id="evolution-template"
                    type="select"
                    value={evolutionTemplateId}
                    onChange={(event) => {
                      const value = event.target.value;
                      setEvolutionTemplateId(value);
                      setSelectedTemplateId(value);
                      if (value === "") {
                        setAnswers({});
                      } else {
                        const template = activeTemplates.find(
                          (item) => item.id === Number(value)
                        );
                        if (template) {
                          setAnswers(emptyAnswersForFields(parseSchemaFields(template.schema_json)));
                        }
                      }
                    }}
                    disabled={submitting || loadingTemplates}
                  >
                    <option value="">{t("clients.evolution.form.templateOptional")}</option>
                    {evolutionTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.title}
                      </option>
                    ))}
                  </Input>
                  <p className="text-muted small mb-0 mt-1">
                    {t("clients.evolution.form.templateHelp")}
                  </p>
                </div>
                {selectedTemplate &&
                evolutionTemplateId !== "" &&
                selectedTemplate.record_type === "evolution"
                  ? schemaFields.map((field) => renderDynamicField(field))
                  : null}
                <div className="d-flex gap-2 mt-4">
                  <Button
                    color="light"
                    type="button"
                    onClick={() => requestConfirm("close")}
                  >
                    {t("clients.anamnesis.actions.close")}
                  </Button>
                  <Button color="primary" type="submit" disabled={submitting}>
                    {submitting ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <i className="ri-save-line align-bottom me-1" />
                    )}
                    {t("clients.evolution.actions.save")}
                  </Button>
                </div>
              </Form>
            ) : loadingTemplates ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner color="primary" />
              </div>
            ) : anamnesisTemplates.length === 0 && formIntent === "create" ? (
              <p className="text-muted mb-0">{t("clients.anamnesis.empty.noTemplates")}</p>
            ) : (
              <Form onSubmit={handleSubmit}>
                {formIntent !== "create" ? (
                  <Alert color={formIntent === "correct" ? "warning" : "info"} className="py-2">
                    <p className="mb-2">
                      {formIntent === "correct"
                        ? t("clients.anamnesis.banner.correct")
                        : t("clients.anamnesis.banner.copy")}
                    </p>
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      type="button"
                      onClick={() => requestConfirm("history")}
                    >
                      {t("clients.anamnesis.actions.backToHistory")}
                    </Button>
                  </Alert>
                ) : null}

                {staleNotice ? (
                  <Alert color="warning" className="py-2">
                    <p className="mb-2">{t("clients.anamnesis.banner.stale")}</p>
                    <Button
                      color="warning"
                      size="sm"
                      type="button"
                      outline
                      onClick={() => requestConfirm("reload")}
                    >
                      {t("clients.anamnesis.actions.loadLatest")}
                    </Button>
                  </Alert>
                ) : null}

                {showRemovedFieldsNotice ? (
                  <Alert color="warning" className="py-2">
                    {t("clients.anamnesis.banner.removedFields")}
                  </Alert>
                ) : null}

                {formIntent === "create" ? (
                  <div className="mb-3">
                    <Label htmlFor="anamnesis-template">{t("clients.anamnesis.fields.template")}</Label>
                    <Input
                      id="anamnesis-template"
                      type="select"
                      value={selectedTemplateId}
                      onChange={(event) => handleTemplateChange(event.target.value)}
                    >
                      <option value="">{t("clients.anamnesis.fields.templatePlaceholder")}</option>
                      {anamnesisTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.title}
                        </option>
                      ))}
                    </Input>
                  </div>
                ) : (
                  <p className="fw-medium mb-3">
                    {selectedTemplate?.title ?? t("clients.anamnesis.common.notAvailable")}
                  </p>
                )}

                {selectedTemplate && schemaFields.length === 0 && Object.keys(orphanAnswers).length === 0 ? (
                  <p className="text-muted">{t("clients.anamnesis.empty.noFields")}</p>
                ) : null}

                {changedFieldCount > 0 ? (
                  <p className="text-muted small mb-3">{t("clients.anamnesis.banner.changed")}</p>
                ) : null}

                <div data-clarity-mask="true">
                  {schemaFields.map((field) => renderDynamicField(field))}
                  {templateRequiresConsent(schemaFields) ? (
                    <div className="mb-3">
                      <Label className="fw-medium">
                        {t("clients.anamnesis.signature.establishmentLabel")}
                      </Label>
                      <p className="text-muted small mb-2">
                        {t("clients.anamnesis.signature.establishmentHint")}
                      </p>
                      {establishmentSignatureDataUrl ? (
                        <p className="text-success small mb-2">
                          <i className="ri-checkbox-circle-line me-1" />
                          {t("clients.anamnesis.signature.establishmentReused")}
                        </p>
                      ) : null}
                      <SignaturePad
                        value={establishmentSignatureDataUrl}
                        onChange={(next) => {
                          setEstablishmentSignatureDataUrl(next);
                          saveEstablishmentSignature(
                            authIdentity.tenantId,
                            authIdentity.userId,
                            next
                          );
                        }}
                        disabled={submitting}
                        height={120}
                      />
                    </div>
                  ) : null}

                  {Object.keys(orphanAnswers).length > 0 ? (
                    <div className="bg-light rounded p-2 mb-3">
                      <p className="fw-medium mb-2">{t("clients.anamnesis.banner.keptAnswers")}</p>
                      {Object.entries(orphanAnswers).map(([key, value]) => (
                        <div key={key} className="mb-2">
                          <span className="fw-medium">{resolveFieldLabel(key, selectedTemplate)}:</span>{" "}
                          <span className="text-muted">
                            {formatAnswerValue(value, key, selectedTemplate)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="d-flex gap-2 mt-4">
                  <Button color="light" type="button" onClick={() => requestConfirm("close")}>
                    {t("clients.anamnesis.actions.close")}
                  </Button>
                  <Button
                    color="primary"
                    type="submit"
                    disabled={
                      submitting
                      || !selectedTemplate
                      || (schemaFields.length === 0 && Object.keys(orphanAnswers).length === 0)
                    }
                  >
                    {submitting ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <i className="ri-save-line align-bottom me-1" />
                    )}
                    {formIntent === "correct"
                      ? t("clients.anamnesis.actions.saveCorrection")
                      : t("clients.anamnesis.actions.save")}
                  </Button>
                </div>
              </Form>
            )}
          </TabPane>

          <TabPane tabId="history">
            {loadingRecords && records.length === 0 ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner color="primary" />
              </div>
            ) : records.length === 0 && appliedQuery === "" && historyQuery.trim() === "" ? (
              <>
                <p className="text-muted mb-3">{t("clients.anamnesis.empty.noRecords")}</p>
                <HelpArticleLink
                  articlePath={HELP_ARTICLE_CORRECT_HEALTH_RECORD}
                  label={t("help.articles.correctHealthRecord")}
                />
              </>
            ) : (
              <>
                {!canExportRecords ? (
                  <Alert color="warning" className="py-2 mb-3">
                    <div className="d-flex align-items-start gap-2">
                      <i className="ri-lock-line fs-18 mt-1 flex-shrink-0" />
                      <div>
                        <p className="mb-1">{t("clients.records.export.noPermission")}</p>
                        <Link to="/settings/team-permissions" className="small fw-medium">
                          {t("clients.records.export.noPermissionCta")}
                        </Link>
                      </div>
                    </div>
                  </Alert>
                ) : (
                  <Alert color="info" className="py-2 mb-3">
                    <div className="d-flex align-items-start gap-2">
                      <i className="ri-printer-line fs-18 mt-1 flex-shrink-0" />
                      <div className="flex-grow-1">
                        <p className="mb-2 small">
                          {records.some((record) => record.has_signed_consent)
                            ? t("clients.records.export.hint.digitalConsent")
                            : t("clients.records.export.hint.signatures")}
                        </p>
                        {selectedExportIds.length > 0 ? (
                          <Button
                            color="primary"
                            size="sm"
                            type="button"
                            disabled={exportingBatch}
                            onClick={() => {
                              void handleBatchExport();
                            }}
                          >
                            {exportingBatch ? (
                              <Spinner size="sm" className="me-1" />
                            ) : (
                              <i className="ri-folder-zip-line align-bottom me-1" />
                            )}
                            {t("clients.records.buttons.exportBatch", {
                              count: selectedExportIds.length,
                            })}
                          </Button>
                        ) : (
                          <p className="mb-0 small text-muted">
                            {t("clients.records.export.hint.batchSelect")}
                          </p>
                        )}
                      </div>
                    </div>
                  </Alert>
                )}

                <div className="d-flex flex-wrap gap-2 mb-3">
                  {(["all", "anamnesis", "evolution"] as RecordTypeFilter[]).map((filter) => (
                    <Button
                      key={filter}
                      color={recordTypeFilter === filter ? "primary" : "light"}
                      size="sm"
                      type="button"
                      onClick={() => setRecordTypeFilter(filter)}
                    >
                      {t(`clients.evolution.filter.${filter}`)}
                    </Button>
                  ))}
                </div>

                {historyEvolutions.length >= 2 ? (
                  <EvolutionSessionsCompare evolutions={historyEvolutions} />
                ) : null}

                {recordsTotal > 1 || historyQuery.trim() !== "" || appliedQuery !== "" || showServiceHistoryTools ? (
                  <div className="mb-3">
                    <Label htmlFor="anamnesis-history-search" className="form-label">
                      {t("clients.anamnesis.history.searchLabel")}
                    </Label>
                    <Input
                      id="anamnesis-history-search"
                      type="search"
                      value={historyQuery}
                      placeholder={t("clients.anamnesis.history.searchPlaceholder")}
                      onChange={(event) => setHistoryQuery(event.target.value)}
                    />
                    <p className="text-muted small mb-0 mt-1">{t("clients.anamnesis.history.searchHint")}</p>
                  </div>
                ) : null}

                {showServiceHistoryTools ? (
                  <div className="mb-3">
                    <Label htmlFor="anamnesis-history-service-filter" className="form-label">
                      {t("clients.anamnesis.history.serviceFilterLabel")}
                    </Label>
                    <Input
                      id="anamnesis-history-service-filter"
                      type="select"
                      value={
                        historyServiceFilter === "all" || historyServiceFilter === "general"
                          ? historyServiceFilter
                          : String(historyServiceFilter)
                      }
                      onChange={(event) => {
                        const value = event.target.value;

                        if (value === "all" || value === "general") {
                          setHistoryServiceFilter(value);
                        } else {
                          setHistoryServiceFilter(Number(value));
                        }
                      }}
                    >
                      <option value="all">{t("clients.anamnesis.history.serviceFilterAll")}</option>
                      <option value="general">{t("clients.anamnesis.history.serviceFilterGeneral")}</option>
                      {catalogServices.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </Input>
                  </div>
                ) : null}

                <div className="mb-3">
                  <HelpArticleLink
                    articlePath={HELP_ARTICLE_CORRECT_HEALTH_RECORD}
                    label={t("help.articles.correctHealthRecord")}
                  />
                </div>

                {records.length === 0 ? (
                  <p className="text-muted mb-0">
                    {appliedQuery !== "" || historyQuery.trim() !== ""
                      ? t("clients.anamnesis.history.searchEmpty")
                      : historyServiceFilter === "general"
                        ? t("clients.anamnesis.history.filterEmptyGeneral")
                        : typeof historyServiceFilter === "number"
                          ? t("clients.anamnesis.history.filterEmptyService")
                          : t("clients.anamnesis.history.searchEmpty")}
                  </p>
                ) : (
                <ListGroup flush>
                {records.map((record) => {
                  const isEvolution = record.record_type === "evolution";
                  const recordTitle = isEvolution
                    ? t("clients.evolution.history.title")
                    : (record.template?.title
                      ?? t("clients.anamnesis.common.notAvailable"));

                  return (
                  <ListGroupItem key={record.id} className="px-0">
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <h6 className="mb-1">
                          <HighlightedTemplateTitle
                            title={recordTitle}
                            query={appliedQuery}
                          />
                        </h6>
                        <p className="text-muted mb-0 fs-12">
                          {wasRecordCorrected(record)
                            ? t("clients.anamnesis.history.updatedAt", {
                              date: formatRecordDate(record.updated_at, i18n.language, salonTimezone),
                            })
                            : t("clients.anamnesis.history.createdAt", {
                              date: formatRecordDate(record.updated_at, i18n.language, salonTimezone),
                            })}
                        </p>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-1">
                        {isEvolution ? (
                          <Badge color="info" className="badge-soft-info text-wrap">
                            {t("clients.evolution.history.badge")}
                          </Badge>
                        ) : null}
                        {(record.attachments_count ?? 0) > 0 ? (
                          <Badge color="secondary" className="badge-soft-secondary text-wrap">
                            {t("clients.evolution.attachments.badge", {
                              count: record.attachments_count,
                            })}
                          </Badge>
                        ) : null}
                        {record.has_signed_consent ? (
                          <Badge color="success" className="badge-soft-success text-wrap">
                            {t("clients.anamnesis.history.badgeConsentSigned")}
                          </Badge>
                        ) : null}
                        {record.consent_term_outdated ? (
                          <Badge color="warning" className="badge-soft-warning text-wrap text-dark">
                            {t("clients.anamnesis.history.badgeConsentOutdated")}
                          </Badge>
                        ) : null}
                        {anamnesisScope === "per_client_service" ? (
                          <Badge
                            color={record.service_id ? "primary" : "secondary"}
                            className={
                              record.service_id
                                ? "badge-soft-primary text-wrap"
                                : "badge-soft-secondary text-wrap"
                            }
                          >
                            {record.service?.name
                              ?? (record.service_id
                                ? t("clients.anamnesis.history.badgeServiceFallback")
                                : t("clients.anamnesis.history.badgeGeneral"))}
                          </Badge>
                        ) : null}
                        {(record.recorded_by?.name || record.professional?.name) ? (
                          <Badge color="info" className="badge-soft-info text-wrap">
                            {record.recorded_by?.name ?? record.professional?.name}
                          </Badge>
                        ) : null}
                      </div>
                    </div>

                    <div className="bg-light rounded p-2 mb-2" data-clarity-mask="true">
                      {isEvolution && record.notes?.trim() ? (
                        <div className="mb-2">
                          <span className="fw-medium">{t("clients.evolution.form.notesLabel")}:</span>{" "}
                          <span className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                            {record.notes}
                          </span>
                        </div>
                      ) : null}
                      {Object.entries(record.answers_json ?? {}).map(([key, value]) => (
                        <div key={key} className="mb-2 last:mb-0">
                          <span className="fw-medium">
                            {resolveFieldLabel(key, record.template)}:
                          </span>{" "}
                          <span className="text-muted">
                            {formatAnswerValue(value, key, record.template)}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <Button
                        color="soft-info"
                        size="sm"
                        type="button"
                        onClick={() =>
                          setExpandedRecordId((current) =>
                            current === record.id ? null : record.id
                          )
                        }
                      >
                        <i className="ri-image-line align-bottom me-1" />
                        {expandedRecordId === record.id
                          ? t("clients.evolution.attachments.hide")
                          : t("clients.evolution.attachments.show")}
                      </Button>
                    </div>

                    {expandedRecordId === record.id ? (
                      <ClientRecordAttachmentsPanel
                        recordId={record.id}
                        clientId={clientId}
                        canUseInMarketing={canUseInMarketing}
                        onAttachmentsChange={() => {
                          void fetchRecords(recordsPage, appliedQuery, false);
                        }}
                      />
                    ) : null}

                    <div className="d-flex flex-wrap gap-2">
                      {canExportRecords ? (
                        <div className="form-check me-1 align-self-center">
                          <Input
                            type="checkbox"
                            className="form-check-input"
                            id={`export-record-${record.id}`}
                            checked={selectedExportIds.includes(record.id)}
                            onChange={() => toggleExportSelection(record.id)}
                          />
                          <Label
                            className="form-check-label visually-hidden"
                            htmlFor={`export-record-${record.id}`}
                          >
                            {t("clients.records.buttons.selectForBatch")}
                          </Label>
                        </div>
                      ) : null}
                      {canCorrectRecord(record) ? (
                        <>
                          <Button
                            color="soft-success"
                            size="sm"
                            type="button"
                            onClick={() => openRecordDraft(record, "correct")}
                          >
                            <i className="ri-edit-line align-bottom me-1" />
                            {t("clients.anamnesis.actions.correct")}
                          </Button>
                          <Button
                            color="soft-secondary"
                            size="sm"
                            type="button"
                            onClick={() => openRecordDraft(record, "copy")}
                          >
                            <i className="ri-file-copy-line align-bottom me-1" />
                            {t("clients.anamnesis.actions.reuse")}
                          </Button>
                        </>
                      ) : null}
                      {canManageRecords && record.has_signed_consent ? (
                        <Button
                          color="soft-warning"
                          size="sm"
                          type="button"
                          onClick={() => {
                            setResignTarget(record);
                            setResignReason("");
                            setResignSignatureDataUrl(null);
                            setResignEstablishmentDataUrl(null);
                          }}
                        >
                          <i className="ri-refresh-line align-bottom me-1" />
                          {t("clients.anamnesis.actions.resign")}
                        </Button>
                      ) : null}
                      {canManageRecords &&
                      anamnesisScope === "per_client_service" &&
                      record.service_id == null ? (
                        <Button
                          color="soft-warning"
                          size="sm"
                          type="button"
                          onClick={() => {
                            setCopySourceRecord(record);
                            setCopyModalOpen(true);
                          }}
                        >
                          <i className="ri-share-forward-line align-bottom me-1" />
                          {t("clients.anamnesis.actions.copyToServices")}
                        </Button>
                      ) : null}
                      {canExportRecords ? (
                        <>
                          <Button
                            color="soft-primary"
                            size="sm"
                            type="button"
                            disabled={exportingRecordId === record.id}
                            onClick={() => handleDownloadPdf(record)}
                          >
                            {exportingRecordId === record.id ? (
                              <Spinner size="sm" className="me-1" />
                            ) : (
                              <i className="ri-download-line align-bottom me-1" />
                            )}
                            {t("clients.records.buttons.download_pdf")}
                          </Button>
                          <Button
                            color="soft-secondary"
                            size="sm"
                            type="button"
                            disabled={exportingRecordId === record.id}
                            onClick={() => handlePrintPdf(record)}
                          >
                            {exportingRecordId === record.id ? (
                              <Spinner size="sm" className="me-1" />
                            ) : (
                              <i className="ri-printer-line align-bottom me-1" />
                            )}
                            {t("clients.records.buttons.print_pdf")}
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </ListGroupItem>
                  );
                })}
              </ListGroup>
                )}

                {recordsPage < recordsLastPage ? (
                  <Button
                    color="light"
                    type="button"
                    className="mt-3"
                    disabled={loadingRecords}
                    onClick={() => {
                      void fetchRecords(recordsPage + 1, appliedQuery, true);
                    }}
                  >
                    {loadingRecords ? <Spinner size="sm" className="me-1" /> : null}
                    {(() => {
                      const remaining = Math.max(0, recordsTotal - records.length);

                      return remaining > 0
                        ? t("clients.anamnesis.history.loadMoreRemaining", {
                          count: remaining,
                        })
                        : t("clients.anamnesis.history.loadMore");
                    })()}
                  </Button>
                ) : null}
              </>
            )}
          </TabPane>
        </TabContent>
      </OffcanvasBody>
    </Offcanvas>

    <Modal isOpen={confirmAction !== null} toggle={() => setConfirmAction(null)} centered>
      <ModalHeader toggle={() => setConfirmAction(null)}>
        {confirmAction === "reload"
          ? t("clients.anamnesis.confirm.reloadTitle")
          : t("clients.anamnesis.confirm.leaveTitle")}
      </ModalHeader>
      <ModalBody>
        {confirmAction === "reload"
          ? t("clients.anamnesis.confirm.reloadText")
          : t("clients.anamnesis.confirm.leaveText")}
      </ModalBody>
      <ModalFooter>
        <Button color="light" type="button" onClick={() => setConfirmAction(null)}>
          {t("clients.anamnesis.confirm.keepEditing")}
        </Button>
        {confirmAction !== null && confirmAction !== "reload" ? (
          <Button
            color="success"
            type="button"
            disabled={submitting}
            onClick={() => {
              const action = confirmAction;
              void (async () => {
                const saved = await persistDraft();

                if (!saved) {
                  return;
                }

                setConfirmAction(null);

                if (action === "close") {
                  onClose();
                  return;
                }

                if (action === "history") {
                  setActiveTab("history");
                }
              })();
            }}
          >
            {submitting ? <Spinner size="sm" className="me-1" /> : null}
            {t("clients.anamnesis.confirm.saveNow")}
          </Button>
        ) : null}
        <Button
          color="primary"
          type="button"
          onClick={() => {
            const action = confirmAction;
            setConfirmAction(null);

            if (action) {
              runConfirmAction(action);
            }
          }}
        >
          {confirmAction === "reload"
            ? t("clients.anamnesis.confirm.reloadConfirm")
            : t("clients.anamnesis.confirm.leaveConfirm")}
        </Button>
      </ModalFooter>
    </Modal>

    <CopyAnamnesisToServicesModal
      isOpen={copyModalOpen}
      onClose={() => {
        setCopyModalOpen(false);
        setCopySourceRecord(null);
      }}
      clientId={clientId}
      clientName={clientName}
      sourceRecord={copySourceRecord}
      onCopied={() => {
        void fetchRecords(1, appliedQuery, false);
        onRecordSaved?.(clientId);
      }}
    />

    <ConsentTermPreviewModal
      isOpen={consentPreview !== null}
      title={consentPreview?.title ?? ""}
      body={consentPreview?.body ?? ""}
      onClose={() => setConsentPreview(null)}
      onConfirmedRead={() => setConsentReadConfirmed(true)}
    />

    <Modal
      isOpen={resignTarget !== null}
      toggle={() => {
        if (!resigning) {
          setResignTarget(null);
        }
      }}
      centered
      size="lg"
    >
      <ModalHeader
        toggle={() => {
          if (!resigning) {
            setResignTarget(null);
          }
        }}
      >
        {t("clients.anamnesis.resign.title")}
      </ModalHeader>
      <ModalBody>
        <Alert color="warning" className="py-2">
          {t("clients.anamnesis.resign.warning")}
        </Alert>
        <div className="mb-3">
          <Label htmlFor="resign-reason">{t("clients.anamnesis.resign.reasonLabel")}</Label>
          <Input
            id="resign-reason"
            type="textarea"
            rows={3}
            value={resignReason}
            onChange={(event) => setResignReason(event.target.value)}
            placeholder={t("clients.anamnesis.resign.reasonPlaceholder")}
            disabled={resigning}
          />
        </div>
        <div className="mb-3">
          <Label className="fw-medium">{t("clients.anamnesis.resign.clientSignature")}</Label>
          <SignaturePad
            value={resignSignatureDataUrl}
            onChange={setResignSignatureDataUrl}
            disabled={resigning}
          />
        </div>
        <div>
          <Label className="fw-medium">
            {t("clients.anamnesis.signature.establishmentLabel")}
          </Label>
          <SignaturePad
            value={resignEstablishmentDataUrl}
            onChange={setResignEstablishmentDataUrl}
            disabled={resigning}
            height={120}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="light"
          type="button"
          disabled={resigning}
          onClick={() => setResignTarget(null)}
        >
          {t("clients.anamnesis.resign.cancel")}
        </Button>
        <Button
          color="warning"
          type="button"
          disabled={resigning}
          onClick={() => {
            void handleResignConsent();
          }}
        >
          {resigning ? <Spinner size="sm" className="me-1" /> : null}
          {t("clients.anamnesis.resign.confirm")}
        </Button>
      </ModalFooter>
    </Modal>
  </>
  );
};

export default ClientAnamnesisOffcanvas;
